<template>
  <div class="login-form">
    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="email">
          <span class="form-group__n">01</span>
          <span>Email address</span>
        </label>
        <input
          id="email"
          v-model="credentials.email"
          type="email"
          placeholder="reporter@pressbox.example"
          required
          :disabled="isLoading"
        />
      </div>

      <div class="form-group">
        <label for="password">
          <span class="form-group__n">02</span>
          <span>Password</span>
        </label>
        <input
          id="password"
          v-model="credentials.password"
          type="password"
          placeholder="••••••••••"
          required
          :disabled="isLoading"
        />
      </div>

      <div v-if="hasError" class="error-message">
        <span class="error-message__mark">✕</span>
        {{ error }}
      </div>

      <button type="submit" class="submit-button" :disabled="isLoading">
        <span v-if="!isLoading">
          Enter the Pressbox
          <span class="submit-button__arrow">&rarr;</span>
        </span>
        <span v-else>Signing you in&hellip;</span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useErrorHandler } from '@/composables/useErrorHandler'
import type { LoginCredentials } from '@/types/auth'

const router = useRouter()
const { login, isLoading } = useAuth()
const { error, hasError, handleError } = useErrorHandler()

const credentials = reactive<LoginCredentials>({
  email: '',
  password: ''
})

const handleSubmit = async (): Promise<void> => {
  const result = await handleError(login(credentials))

  if (result !== null) {
    const redirect = router.currentRoute.value.query.redirect as string
    await router.push(redirect || '/dashboard')
  }
}
</script>

<style scoped>
.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  margin-bottom: 0.55rem;
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink);
}

.form-group__n {
  font-family: var(--font-data);
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--ox);
  letter-spacing: 0;
}

input {
  width: 100%;
  padding: 0.85rem 0;
  font-family: var(--font-editorial);
  font-size: 1.05rem;
  color: var(--ink);
  background: transparent;
  border: none;
  border-bottom: var(--rule-thin) solid var(--ink);
  transition:
    border-color 0.15s ease,
    padding 0.18s ease;
}

input::placeholder {
  color: var(--ink-ghost);
  font-style: italic;
}

input:focus {
  outline: none;
  border-bottom-color: var(--ox);
  border-bottom-width: var(--rule-med);
  padding-bottom: calc(0.85rem - 1px);
}

input:disabled {
  color: var(--ink-ghost);
  cursor: not-allowed;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  background: var(--ox-10);
  border-left: var(--rule-thick) solid var(--ox);
  font-family: var(--font-editorial);
  font-size: 0.9rem;
  color: var(--ox-deep);
}

.error-message__mark {
  font-family: var(--font-ui);
  font-weight: 700;
  color: var(--ox);
}

.submit-button {
  width: 100%;
  margin-top: 0.5rem;
  padding: 1rem 1.25rem;
  font-family: var(--font-ui);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--paper);
  background: var(--ink);
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition:
    background 0.18s ease,
    transform 0.12s ease;
  box-shadow: 3px 3px 0 var(--ox);
}

.submit-button:hover:not(:disabled) {
  background: var(--ox);
  box-shadow: 3px 3px 0 var(--ink);
}

.submit-button:active:not(:disabled) {
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 var(--ink);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-button__arrow {
  display: inline-block;
  transition: transform 0.18s ease;
}

.submit-button:hover:not(:disabled) .submit-button__arrow {
  transform: translateX(4px);
}
</style>
